<script setup lang="ts">
import { ArrowLeft, ArrowRight } from 'lucide-vue-next';

import { PAGE_SIZE_OPTIONS } from '~/constants';

type PaginationEmits = {
  'page-change': [page: number];
  'page-size-change': [pageSize: number];
};

type PaginationProps = {
  currentPage: number;
  hasNext: boolean;
  minPageSize?: number;
  pageSize: number;
};

const ICON_HEIGHT = '12';
const PAGINATION_LABELS = {
  ITEMS_PER_PAGE: 'Itens por página',
  NEXT: 'Próxima',
  PREVIOUS: 'Anterior',
} as const;

const props = withDefaults(defineProps<PaginationProps>(), {
  minPageSize: 5,
});
const emit = defineEmits<PaginationEmits>();

const filteredPageSizeOptions = Object.entries(PAGE_SIZE_OPTIONS).filter(
  ([value]) => Number(value) >= props.minPageSize,
);

function handleNextPage(): void {
  emit('page-change', props.currentPage + 1);
}

function handlePageSizeChange(event: Event): void {
  const target = event.target as HTMLSelectElement;
  const newPageSize = Number(target.value);
  emit('page-size-change', newPageSize);
}

function handlePreviousPage(): void {
  emit('page-change', props.currentPage - 1);
}
</script>

<template>
  <div class="d-flex justify-content-end gap-2 my-3 pe-5 w-full">
    <div class="d-flex">
      <label class="form-label w-100" for="pageSize">{{ PAGINATION_LABELS.ITEMS_PER_PAGE }}</label>
      <select
        id="pageSize"
        class="form-select"
        :value="pageSize"
        @change="handlePageSizeChange"
      >
        <option v-for="[value, label] in filteredPageSizeOptions" :value="value" :key="value">
          {{ label }}
        </option>
      </select>
    </div>
    <div class="d-flex gap-3">
      <button
        type="button"
        class="btn btn-secondary"
        :disabled="currentPage === 1"
        @click="handlePreviousPage"
      >
        <ArrowLeft :height="ICON_HEIGHT" /> {{ PAGINATION_LABELS.PREVIOUS }}
      </button>
      <button
        type="button"
        class="btn btn-primary"
        :disabled="!hasNext"
        @click="handleNextPage"
      >
        {{ PAGINATION_LABELS.NEXT }}
        <ArrowRight :height="ICON_HEIGHT" />
      </button>
    </div>
  </div>
</template>
